import { type QueryClient, queryOptions, useQuery } from "@tanstack/vue-query";
import { useCacheTimings } from "~/composables/cacheTiming";
import {
  type AccessibleEntity,
  Chapter,
  type ChapterEntity,
  type ChapterListParams,
  type EntityAccessEntity,
  type IncludeArr,
  isExpanded,
  NovelBookmark,
  type NovelBookmarkListParams,
  NovelChapter,
  type NovelChapterEntity,
  type NovelChapterListParams,
} from "~/src/api";
import { checkEntityAccess } from "~/src/gating";
import { type AuthFunction, toUnordered } from "~/utils/tanstack/global";
import { natSortNulls } from "~/src/reader/utils";

export const getNovelChapterFromCache = (
  client: () => QueryClient,
  chapterId: string,
  includes: IncludeArr<ChapterEntity> = [],
  authed = false,
) => {
  const qc = client();
  const cache = qc.getQueryCache();
  return cache.find({
    queryKey: [
      "chapter",
      "entity",
      chapterId,
      {
        includes: toUnordered("includes", includes),
        authed: authed || undefined,
      },
    ],
    exact: false,
  })?.state.data as ChapterEntity | undefined;
};

export const cacheNovelChapter = (
  client: () => QueryClient,
  chapter: NovelChapterEntity,
  authed = false,
) => {
  const qc = client();
  const expandedRelations = new Set(
    chapter.relationships.filter(isExpanded).map((rel) => rel.type),
  );

  qc.setQueryData(
    [
      "novel_chapter",
      "entity",
      chapter.id,
      {
        includes: toUnordered("includes", Array.from(expandedRelations)),
        authed: authed || undefined,
      },
    ],
    chapter,
  );
};

export const useNovelChapterList = (
  params: MaybeRefOrGetter<NovelChapterListParams> = {},
  auth?: AuthFunction,
) => {
  const unorderedParams = computed(() => {
    const paramsValue = toValue(params);
    return {
      ...paramsValue,
      includes: paramsValue.includes
        ? toUnordered("includes", paramsValue.includes)
        : undefined,
      translatedLanguages: paramsValue.translatedLanguages
        ? toUnordered("langs", paramsValue.translatedLanguages)
        : undefined,
      ids: paramsValue.ids ? toUnordered("ids", paramsValue.ids) : undefined,
      titleIds: paramsValue.titleIds
        ? toUnordered("titleIds", paramsValue.titleIds)
        : undefined,
      states: paramsValue.states
        ? toUnordered("states", paramsValue.states)
        : undefined,
      authed: !!auth || undefined,
    };
  });

  return queryOptions({
    queryKey: ["novel_chapter", "collection", unorderedParams],
    queryFn: async ({ client }) => {
      const paramsValue = toValue(params);
      const res = await NovelChapter.find(
        paramsValue,
        (await auth?.()) || undefined,
      );
      // cache for individual entities returned by the list endpoint
      res.data.forEach((chapter) =>
        cacheNovelChapter(() => client as QueryClient, chapter, !!auth),
      );
      return res;
    },
    staleTime: useCacheTimings().collection.chapter,
  });
};

export const useNovelChapterGet = (
  id: MaybeRefOrGetter<string>,
  includes: IncludeArr<NovelChapterEntity> = [],
  auth?: AuthFunction,
) => {
  return queryOptions({
    queryKey: [
      "novel_chapter",
      "entity",
      id,
      {
        includes: toUnordered("includes", includes),
        authed: !!auth || undefined,
      },
    ],
    queryFn: async () => {
      const idValue = toValue(id);
      if (!idValue) throw new Error("Chapter Id unspecified");
      if (idValue === "unknown") throw new Error("Chapter Unknown");
      return await NovelChapter.get(
        idValue,
        includes,
        (await auth?.()) || undefined,
      );
    },
    staleTime: useCacheTimings().entity.chapter,
  });
};

export const useNovelBookmarks = (
  params: MaybeRefOrGetter<NovelBookmarkListParams> = {},
) => {
  const unorderedParams = computed(() => ({
    ...toValue(params),
    authed: true,
  }));

  return queryOptions({
    queryKey: ["novel_chapter_bookmark", "collection", unorderedParams],
    queryFn: async () => {
      return await NovelBookmark.find(
        {
          ...{
            ...unorderedParams.value,
            authed: undefined,
          },
        },
        await getTokenOrThrow(),
      );
    },
    staleTime: useCacheTimings().collection.novel_chapter_bookmark,
  });
};

export const useNovelReaderTitleChapters = (
  titleId: MaybeRefOrGetter<string>,
) => {
  return queryOptions({
    ...useTitleGroupedChapters(titleId, { order: { natural: "desc" } }),
    select: (data) => ({
      ...data,
      attributes: {
        ...data.attributes,
        // this is here to ensure order consistency
        list: data.attributes.list
          .map((vol) => ({
            ...vol,
            novelChapters: vol.novelChapters.sort(
              ({ chapter: chA }, { chapter: chB }) => natSortNulls(chA, chB),
            ),
          }))
          .sort(({ volume: volumeA }, { volume: volumeB }) =>
            natSortNulls(volumeA, volumeB),
          ),
      },
    }),
  });
};
